import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

// Interfaces
import { IAdvisorsRankingConfiguration } from "./interfaces";

// API
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// MUI
import { Box, Stack } from "@mui/material";

// Common Components
import Title from "../../../common/Title";
import AdvisorsRanking from "./AdvisorsRanking";

const AdvisorsRankingConfiguration = (
	props: Readonly<{ locationId: string; rankingConfiguration?: IAdvisorsRankingConfiguration | null }>
) => {
	const { locationId, rankingConfiguration } = props;
	const [innerConfiguration, setInnerConfiguration] = useState<IAdvisorsRankingConfiguration | null>(null);

	const { mutate, isPending } = useMutation({
		mutationFn: (input: { advisorsRankingConfiguration: IAdvisorsRankingConfiguration }) =>
			updateSystemSettings(locationId, input, "ADVISORS_RANKING_CONFIGURATION"),
		onError: (error) => errorLogger(error),
	});

	const update = (configuration: IAdvisorsRankingConfiguration) => {
		mutate({ advisorsRankingConfiguration: configuration });
	};

	const handleUpdateConfiguration = (updated: IAdvisorsRankingConfiguration) => {
		setInnerConfiguration(updated);
		update(updated);
	};

	useEffect(() => {
		setInnerConfiguration({ isRankingRO: false, category: "global", ...rankingConfiguration });
		return () => {
			setInnerConfiguration(null);
		};
	}, [locationId, rankingConfiguration]);

	return (
		<Box>
			<Title title="Advisor Ranking" className="!mb-[5px]" />
			<Title
				title="Setting that defines whether Advisor rankings will use R0/hrs or Effective Labor Rate ranking data."
				className="!font-sans !text-[14.5px] !mb-[24px]"
			/>
			{/* Setting that defines whether Advisor rankings will use R0/hrs or Effective Labor Rate ranking data. */}
			<Stack mt="15px">
				<AdvisorsRanking
					category={innerConfiguration?.category ?? "global"}
					isRankingRO={innerConfiguration?.isRankingRO ?? false}
					readOnly={false}
					saving={isPending}
					onActionUpdate={(data) => handleUpdateConfiguration(data as IAdvisorsRankingConfiguration)}
				/>
			</Stack>
		</Box>
	);
};

export default AdvisorsRankingConfiguration;
